import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { login } from "../api/emailAuthApi";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  CardContent,
  Button,
  Input,
} from "../components/ui";
import { useToast } from "../context/ToastContext";

const Login = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const toast = useToast();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      console.log("========== LOGIN DEBUG START ==========");
      console.log("[DEBUG] API base URL env var:", process.env.REACT_APP_API_URL);
      console.log("[DEBUG] Current page origin:", window.location.origin);
      console.log("[DEBUG] document.cookie BEFORE login:", document.cookie || "(empty)");

      const loginRes = await login(email, password);

      console.log("[DEBUG] login() response data:", loginRes?.data ?? loginRes);
      console.log("[DEBUG] login() response headers:", loginRes?.headers);
      console.log("[DEBUG] login() response status:", loginRes?.status);
      console.log("[DEBUG] document.cookie AFTER login:", document.cookie || "(empty — expected if httpOnly)");

      const authData = await refreshUser();

      console.log("[DEBUG] refreshUser() authData:", authData);

      if (!authData) {
        console.warn(
          "[DEBUG] refreshUser() returned falsy — this means /auth/me either " +
          "failed, returned 401, or the request didn't include the cookie. " +
          "Check the Network tab: click the /auth/me request → Request Headers " +
          "→ is a 'Cookie' header present? If not, the cookie isn't being sent."
        );
      }

      console.log("========== LOGIN DEBUG END ==========");

      if (authData?.hasSchool) {
        navigate("/school-home");
      } else {
        navigate("/school-setup");
      }
    } catch (err) {
      console.log("========== LOGIN ERROR DEBUG ==========");
      console.log("[DEBUG] err.message:", err?.message);
      console.log("[DEBUG] err.code:", err?.code);
      console.log("[DEBUG] err.response?.status:", err?.response?.status);
      console.log("[DEBUG] err.response?.data:", err?.response?.data);
      console.log("[DEBUG] err.response?.headers:", err?.response?.headers);
      console.log("[DEBUG] err.config?.url:", err?.config?.url);
      console.log("[DEBUG] err.config?.baseURL:", err?.config?.baseURL);
      console.log("[DEBUG] err.config?.withCredentials:", err?.config?.withCredentials);
      if (!err?.response) {
        console.warn(
          "[DEBUG] No err.response at all — this usually means the request " +
          "never reached the server (CORS block, wrong URL, network error, " +
          "or mixed-content http/https block). Check the Network tab and the " +
          "Console tab for a red CORS error message."
        );
      }
      console.log("========================================");

      toast.error(
        err?.response?.data?.message ||
          "Invalid credentials. Please verify your email and password."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-neutral-50 dark:bg-neutral-950">
      {/* Logo */}
      <div className="px-5 py-5 sm:px-8 sm:py-6">
        <Link to="/" className="inline-flex items-center">
          <img
            src="https://res.cloudinary.com/hsvfv0h8/image/upload/v1786152037/Untitled_design__6_-removebg-preview_zijzg4.png"
            alt="EduFlow"
            className="h-11 w-auto object-contain sm:h-12"
          />
        </Link>
      </div>

      {/* Authentication */}
      <main className="flex justify-center px-5 pb-10 pt-5 sm:px-6 sm:pt-7">
        <div className="w-full max-w-[400px]">
          <Card className="rounded-xl border border-neutral-200 bg-white shadow-none dark:border-neutral-800 dark:bg-neutral-900">
            <CardContent className="p-7 sm:p-8">
              {/* Header */}
              <div className="mb-7">
                <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
                  Welcome Back!
                </h1>

                <p className="mt-1.5 text-sm leading-5 text-neutral-500 dark:text-neutral-400">
                  Sign in with your credentials to access
                </p>
              </div>

              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email */}
                <div className="px-0.5">
                  <Input
                    label="Email Address"
                    id="email"
                    type="email"
                    name="email"
                    placeholder="name@institution.edu"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    className="h-11 px-3 text-sm"
                  />
                </div>

                {/* Password */}
                <div className="space-y-2 px-0.5">
                  <div className="flex items-center justify-between">
                    <label
                      className="block text-xs font-medium text-neutral-700 dark:text-neutral-200"
                      htmlFor="password"
                    >
                      Password
                    </label>

                    <Link
                      to="/forgot-password"
                      className="text-xs font-medium text-primary-600 transition-colors hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      Forgot Password?
                    </Link>
                  </div>

                  <Input
                    id="password"
                    type="password"
                    name="password"
                    placeholder="Enter your password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    className="h-11 px-3 text-sm"
                  />
                </div>

                {/* Submit */}
                <div className="pt-1">
                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    isLoading={loading}
                    className="h-11 text-sm font-medium"
                  >
                    Sign In
                  </Button>
                </div>
              </form>

              {/* Divider */}
              <div className="relative my-7 flex items-center justify-center">
                <div className="w-full border-t border-neutral-100 dark:border-neutral-800" />

                <span className="absolute bg-white px-3 text-[10px] font-medium uppercase tracking-wider text-neutral-400 dark:bg-neutral-900 dark:text-neutral-500">
                  Or continue with
                </span>
              </div>

              {/* Google */}
              <Button
                type="button"
                variant="outline"
                className="h-11 w-full gap-2.5 border-neutral-200/80 text-sm font-medium text-neutral-700 dark:border-neutral-800 dark:text-neutral-200"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>

                <span>Sign in with Google</span>
              </Button>

              {/* Sign Up */}
              <p className="pt-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
                Don't have an account?{" "}
                <Link
                  to="/signup"
                  className="font-semibold text-neutral-900 hover:underline dark:text-neutral-100"
                >
                  Sign up
                </Link>
              </p>
            </CardContent>
          </Card>

          {/* Footer */}
          <footer className="mt-6 space-y-1 text-center text-[11px] text-neutral-400 dark:text-neutral-500">
            <p>
              © {new Date().getFullYear()} TFC elevate. All rights reserved.
            </p>

            <div className="flex items-center justify-center gap-3 pt-1">
              <a
                href="#privacy"
                className="transition-colors hover:text-neutral-600 dark:hover:text-neutral-300"
              >
                Privacy Policy
              </a>

              <span>•</span>

              <a
                href="#terms"
                className="transition-colors hover:text-neutral-600 dark:hover:text-neutral-300"
              >
                Terms of Service
              </a>

              <span>•</span>

              <a
                href="#help"
                className="transition-colors hover:text-neutral-600 dark:hover:text-neutral-300"
              >
                Help Center
              </a>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default Login;